import type { ReactNode } from 'react';
import {
  AlertTriangle,
  Calendar,
  CalendarCheck,
  CalendarX,
  Check,
  ClipboardList,
  Clock,
  FileText,
  Paperclip,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import type { CourseStepVisual } from '@/lib/courseStepStatus';
import { formatCourseWindowRange, isSameCourseDay } from '@/lib/courseDateFormat';
import {
  courseWindowCivils,
  isCivilInCourseWindow,
  type CourseIntervalWindow,
} from '@/lib/courseIntervalWindow';
import { civilDateFromIso } from '@/lib/treatmentCourseWizard';

export function CourseTabPanel({
  children,
  className,
  testId,
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <section
      className={cn('fl card tabcard rounded-card bg-ds-card p-s5 shadow-card overflow-hidden', className)}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

export function CourseStepMarker({
  visual,
  size = 'md',
  action = false,
}: {
  visual: CourseStepVisual;
  size?: 'sm' | 'md';
  action?: boolean;
}) {
  const dim = size === 'sm' ? 'sm' : '';
  if (action && visual !== 'done') {
    return (
      <span className={cn('mk warn', dim)} aria-hidden>
        <AlertTriangle className="i xs" />
      </span>
    );
  }
  switch (visual) {
    case 'done':
      return (
        <span className={cn('mk done', dim)} aria-hidden>
          <Check className="i xs" />
        </span>
      );
    case 'next':
      return <span className={cn('mk next', dim)} aria-hidden />;
    case 'planned':
      return <span className={cn('mk plan', dim)} aria-hidden />;
    default: {
      const _exhaustive: never = visual;
      return _exhaustive;
    }
  }
}

export function CourseSegmentBar({
  total,
  completed,
  currentIndex,
  faded = false,
}: {
  total: number;
  completed: number;
  currentIndex?: number;
  faded?: boolean;
}) {
  const count = Math.max(total, 0);
  return (
    <div
      className={cn('segbar', faded && 'off')}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={count}
      aria-valuenow={completed}
    >
      {Array.from({ length: count }, (_, index) => {
        const done = index < completed;
        const next = !done && (currentIndex ?? completed) === index;
        return <i key={index} className={cn(done && 'd', next && !faded && 'n')} />;
      })}
    </div>
  );
}

export function CourseTodayChip() {
  const { t } = useTranslation();
  return <span className="chip chip-today">{t('treatment_courses.today_chip')}</span>;
}

export function CourseVisualChip({
  visual,
  action = false,
}: {
  visual: CourseStepVisual;
  action?: boolean;
}) {
  const { t } = useTranslation();
  if (action) {
    return (
      <span className="chip chip-amber">
        <AlertTriangle className="i xs" aria-hidden />
        {t('treatment_courses.visual_action')}
      </span>
    );
  }
  switch (visual) {
    case 'done':
      return (
        <span className="chip chip-green">
          <Check className="i xs" aria-hidden />
          {t('treatment_courses.visual_done')}
        </span>
      );
    case 'next':
      return (
        <span className="chip chip-strong">
          <Clock className="i xs" aria-hidden />
          {t('treatment_courses.visual_next')}
        </span>
      );
    case 'planned':
      return (
        <span className="chip chip-neutral">
          <Calendar className="i xs" aria-hidden />
          {t('treatment_courses.visual_planned')}
        </span>
      );
    default: {
      const _exhaustive: never = visual;
      return _exhaustive;
    }
  }
}

/**
 * Booking label shown beside Planlagt.
 * Planlagt means the step is later in the course, not that a time is booked.
 */
export function CoursePlannedBookingChip({
  visual,
  startsAt,
}: {
  visual: CourseStepVisual;
  startsAt?: string | null;
}) {
  const { t } = useTranslation();
  if (visual !== 'planned') return null;
  const booked = typeof startsAt === 'string' && startsAt.length > 0;
  if (booked) {
    return (
      <span className="chip chip-teal" data-testid="course-booking-chip" data-booked="true">
        <CalendarCheck className="i xs" aria-hidden />
        {t('treatment_courses.appt_status_booked')}
      </span>
    );
  }
  return (
    <span className="chip chip-amber" data-testid="course-booking-chip" data-booked="false">
      <CalendarX className="i xs" aria-hidden />
      {t('treatment_courses.book_not_booked')}
    </span>
  );
}

export function CourseStatusChip({
  status,
}: {
  status: 'active' | 'completed' | 'cancelled';
}) {
  const { t } = useTranslation();
  switch (status) {
    case 'active':
      return (
        <span className="chip chip-teal">
          <Clock className="i xs" aria-hidden />
          {t('treatment_courses.patient_status_active')}
        </span>
      );
    case 'completed':
      return (
        <span className="chip chip-green">
          <Check className="i xs" aria-hidden />
          {t('treatment_courses.patient_status_completed')}
        </span>
      );
    case 'cancelled':
      return (
        <span className="chip chip-neutral">
          {t('treatment_courses.patient_status_cancelled')}
        </span>
      );
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function CourseBreadcrumb({
  items,
}: {
  items: Array<{ label: string; onClick?: () => void }>;
}) {
  return (
    <nav className="fl-crumb" aria-label="breadcrumb">
      {items.map((item, index) => (
        <span key={`${item.label}-${index}`} className="inline-flex items-center gap-[6px]">
          {index > 0 ? (
            <span className="sep" aria-hidden>
              /
            </span>
          ) : null}
          {item.onClick ? (
            <button type="button" onClick={item.onClick} className="link">
              {index === 0 ? (
                <>
                  <svg className="i xs" viewBox="0 0 24 24" aria-hidden>
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  {item.label}
                </>
              ) : (
                item.label
              )}
            </button>
          ) : (
            <span>{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function CourseWindowScale({
  window,
  startsAt,
}: {
  window: CourseIntervalWindow;
  startsAt?: string | null;
}) {
  const selectedCivil = civilDateFromIso(startsAt ?? null) ?? window.centerCivil;
  const days = courseWindowCivils(window);
  return (
    <span className="win" aria-hidden>
      {days.map((civil) => {
        const day = Number(civil.slice(8, 10));
        const selected = civil === selectedCivil;
        return (
          <span key={civil} className={selected ? 'c' : undefined}>
            {day}
          </span>
        );
      })}
    </span>
  );
}

export function CourseIntervalStrip({
  label,
  window,
  startsAt,
  closedDays,
}: {
  label: string;
  window: CourseIntervalWindow | null;
  startsAt?: string | null;
  closedDays?: string;
}) {
  const { t, i18n } = useTranslation();
  const hasWindow = window != null;
  const inWindow = hasWindow
    ? isCivilInCourseWindow(civilDateFromIso(startsAt ?? null) ?? window.centerCivil, window)
    : true;
  return (
    <div className="ivl num">
      <span className="rule">
        <svg className="i xs" viewBox="0 0 24 24" aria-hidden>
          <path d="m17 2 4 4-4 4" />
          <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
          <path d="m7 22-4-4 4-4" />
          <path d="M21 13v1a4 4 0 0 1-4 4H3" />
        </svg>
        {label}
      </span>
      {hasWindow ? <CourseWindowScale window={window} startsAt={startsAt} /> : null}
      {hasWindow ? (
        <span>
          {t('treatment_courses.window_label', {
            range: formatCourseWindowRange(window.startIso, window.endIso, i18n.language),
          })}
        </span>
      ) : null}
      {startsAt && hasWindow ? (
        <span className={inWindow ? 'ok' : undefined}>
          {inWindow ? <Check className="i xs" aria-hidden /> : <AlertTriangle className="i xs" aria-hidden />}
          {inWindow ? t('treatment_courses.window_in') : t('treatment_courses.window_out')}
        </span>
      ) : null}
      {closedDays ? <span>{t('treatment_courses.window_closed_days', { days: closedDays })}</span> : null}
    </div>
  );
}

export function CourseCount({ value }: { value: number }) {
  return <span className="tcount num">{value}</span>;
}

export function CourseEmptyBlock({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="pane-e">
      <span className="ic">{icon}</span>
      <div>
        <p className="e1">{title}</p>
        {description ? <p className="e2">{description}</p> : null}
        {action ? <div className="btns">{action}</div> : null}
      </div>
    </div>
  );
}

export function CourseMetaChip({
  kind,
  label,
  value,
  warn = false,
  ok = false,
  iconWarn = false,
}: {
  kind: 'journal' | 'questionnaire' | 'files';
  label: string;
  value: string;
  warn?: boolean;
  ok?: boolean;
  /** Amber icon only. The chip fill and label stay neutral. Ignored while `warn` is set. */
  iconWarn?: boolean;
}) {
  const Icon =
    kind === 'journal' ? FileText : kind === 'files' ? Paperclip : ClipboardList;
  return (
    <span className={cn('ck2', warn && 'wa', !warn && iconWarn && 'icon-amber', ok && 'ok')}>
      <Icon className="i xs" aria-hidden />
      <span className="lb">{label}:</span>
      {value}
    </span>
  );
}

export function courseTodayPrefix(
  iso: string | null | undefined,
  todayLabel: string,
): string {
  return isSameCourseDay(iso) ? `${todayLabel}, ` : '';
}
